<template>
  <div class="chart-time-filter">
    <label class="chart-time-filter__label">Rango de tiempo</label>

    <div class="chart-time-filter__row">
      <!-- Preset chips (relative ranges) -->
      <div class="chart-time-filter__chips">
        <button
          v-for="opt in presets"
          :key="opt.value"
          type="button"
          class="chart-time-filter__chip"
          :class="{ 'chart-time-filter__chip--active': from === opt.value && !calendarOpen }"
          @click="selectPreset(opt.value)"
        >
          {{ opt.label }}
        </button>
      </div>

      <!-- Calendar toggle button -->
      <button
        type="button"
        class="chart-time-filter__cal-btn"
        :class="{ 'chart-time-filter__cal-btn--active': calendarOpen }"
        :title="'Rango personalizado'"
        @click="calendarOpen = !calendarOpen"
      >
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </button>
    </div>

    <!-- Calendar date pickers (animated reveal) -->
    <transition name="chart-time-cal">
      <div v-if="calendarOpen" class="chart-time-filter__calendar">
        <div class="chart-time-filter__cal-field">
          <span class="chart-time-filter__cal-label">Desde</span>
          <flat-pickr
            :value="from"
            @on-change="(dates, dateStr) => onCalendarFrom(dateStr)"
            :config="flatpickrConfig"
            class="form-control chart-time-filter__datepicker"
            :placeholder="'Seleccionar fecha...'"
          />
        </div>
        <div class="chart-time-filter__cal-field">
          <span class="chart-time-filter__cal-label">Hasta</span>
          <flat-pickr
            :value="to"
            @on-change="(dates, dateStr) => onCalendarTo(dateStr)"
            :config="flatpickrConfig"
            class="form-control chart-time-filter__datepicker"
            :placeholder="'Seleccionar fecha...'"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import flatPickr from 'vue-flatpickr-component'
import { OPEN_TIME_RANGE_FROM } from '../utils/timeUtils'

const PRESETS = [
  { value: 'now-15m', label: '15m' },
  { value: 'now-1h',  label: '1h' },
  { value: 'now-6h',  label: '6h' },
  { value: 'now-24h', label: '24h' },
  { value: 'now-7d',  label: '7d' },
  { value: 'now-30d', label: '30d' },
  { value: 'now-90d', label: '90d' },
  { value: OPEN_TIME_RANGE_FROM, label: 'Todo' },
]

export default {
  name: 'ChartTimeFilter',

  components: { flatPickr },

  props: {
    from: { type: String, default: '' },
    to: { type: String, default: '' },
    t: { type: Function, required: true },
  },

  data() {
    return {
      calendarOpen: false,
      flatpickrConfig: {
        enableTime: true,
        dateFormat: 'Y-m-d H:i:s',
        time_24hr: true,
      },
    }
  },

  computed: {
    presets() {
      return PRESETS
    },
  },

  watch: {
    from: {
      immediate: true,
      handler(val) {
        // An absolute date means a custom range unless it is a preset of its
        // own (the open range), which would otherwise open the calendar.
        if (val && !val.startsWith('now') && !PRESETS.some(p => p.value === val)) {
          this.calendarOpen = true
        }
      },
    },
  },

  methods: {
    selectPreset(value) {
      this.calendarOpen = false
      this.$emit('update:from', value)
      this.$emit('update:to', '')
    },

    onCalendarFrom(dateStr) {
      this.$emit('update:from', dateStr)
    },

    onCalendarTo(dateStr) {
      this.$emit('update:to', dateStr)
    },
  },
}
</script>

<style scoped>
.chart-time-filter {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.chart-time-filter__label {
  display: block;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.7;
}

.chart-time-filter__row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.chart-time-filter__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.chart-time-filter__chip {
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
  color: inherit;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.chart-time-filter__chip:hover {
  background: rgba(0, 104, 201, 0.2);
  border-color: rgba(0, 104, 201, 0.4);
}

.chart-time-filter__chip--active {
  background: #0068c9;
  border-color: #0068c9;
  color: #fff;
}

.chart-time-filter__cal-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.06);
  color: inherit;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.chart-time-filter__cal-btn:hover {
  background: rgba(0, 104, 201, 0.2);
  border-color: rgba(0, 104, 201, 0.4);
}

.chart-time-filter__cal-btn--active {
  background: rgba(0, 104, 201, 0.25);
  border-color: #0068c9;
  color: #4da6ff;
}

/* Calendar section */
.chart-time-filter__calendar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: flex-end;
  padding-top: 6px;
}

.chart-time-filter__cal-field {
  flex: 1 1 160px;
  min-width: 140px;
  max-width: 240px;
}

.chart-time-filter__cal-label {
  display: block;
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 3px;
  opacity: 0.55;
}

.chart-time-filter__datepicker {
  font-size: 0.82rem;
  padding: 5px 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.06);
  color: inherit;
  min-height: 32px;
  width: 100%;
}

/* Calendar slide transition */
.chart-time-cal-enter-active,
.chart-time-cal-leave-active {
  transition: all 0.25s ease;
  overflow: hidden;
}

.chart-time-cal-enter,
.chart-time-cal-leave-to {
  opacity: 0;
  max-height: 0;
  padding-top: 0;
}

.chart-time-cal-enter-to,
.chart-time-cal-leave {
  max-height: 100px;
  opacity: 1;
}
</style>
